import type { ChatMessage } from '../../../shared/types';
import type { ChatAdapterConfig, ChatProtocolAdapter, ChatAdapterStatus } from './types';
import { generateAnonymousNick, sanitizeNick, senderColor } from './nick';

/**
 * Lightweight Matrix public room adapter using Client-Server API /sync (guest read / public room).
 */
export class MatrixAdapter implements ChatProtocolAdapter {
  private isManuallyClosed: boolean = false;
  private pollAbortController: AbortController | null = null;
  private nextBatchToken: string | null = null;
  private roomId: string = '';
  private homeserver: string = '';

  currentNick: string = '';
  onMessage: (msg: ChatMessage) => void = () => {};
  onStatus: (status: ChatAdapterStatus, detail?: string) => void = () => {};
  onError: (err: Error) => void = () => {};

  async connect(config: ChatAdapterConfig): Promise<void> {
    this.isManuallyClosed = false;

    const initialNick = config.nick ? sanitizeNick(config.nick) : generateAnonymousNick();
    this.currentNick = initialNick;

    // Room ID or space
    this.roomId = config.channel || config.space || '';
    // Homeserver URL, e.g. matrix.org or https://matrix.org
    let server = config.server || 'matrix.org';
    if (!server.startsWith('http://') && !server.startsWith('https://')) {
      server = `https://${server}`;
    }
    this.homeserver = server.replace(/\/+$/, '');

    this.onStatus('connecting');

    try {
      this.pollAbortController = new AbortController();
      // Test initial sync or public room messages
      await this.syncOnce(true);
      this.onStatus('connected');
      this.startPollingLoop();
    } catch (err: any) {
      this.onError(err);
      this.onStatus('error', err?.message || 'Failed to connect to Matrix room');
      throw err;
    }
  }

  async sendMessage(text: string): Promise<void> {
    const cleanText = text.trim();
    if (!cleanText) return;

    // For public guest / unauthenticated adapters, emitting local chat message
    const localMsg: ChatMessage = {
      id: `matrix-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      text: cleanText,
      displayName: this.currentNick,
      timestamp: Date.now(),
      protocol: 'matrix',
      senderColor: senderColor(this.currentNick),
    };
    this.onMessage(localMsg);
  }

  async changeNick(newNick: string): Promise<void> {
    this.currentNick = sanitizeNick(newNick);
  }

  disconnect(): void {
    this.isManuallyClosed = true;
    if (this.pollAbortController) {
      this.pollAbortController.abort();
      this.pollAbortController = null;
    }
    this.onStatus('disconnected');
  }

  private async syncOnce(initial: boolean = false): Promise<void> {
    const params = new URLSearchParams();
    params.set('timeout', initial ? '0' : '30000');
    if (this.nextBatchToken) {
      params.set('since', this.nextBatchToken);
    }
    // Filter to only room events if room is known
    const url = `${this.homeserver}/_matrix/client/v3/sync?${params.toString()}`;
    const res = await fetch(url, {
      signal: this.pollAbortController?.signal,
    });

    if (!res.ok) {
      throw new Error(`Matrix sync failed with HTTP ${res.status}`);
    }

    const data = await res.json();
    this.nextBatchToken = data.next_batch || null;

    if (data.rooms?.join) {
      for (const [roomId, roomData] of Object.entries<any>(data.rooms.join)) {
        if (this.roomId && roomId !== this.roomId && !this.roomId.includes(roomId)) {
          continue;
        }
        const timelineEvents = roomData?.timeline?.events || [];
        for (const evt of timelineEvents) {
          if (evt.type === 'm.room.message' && evt.content?.msgtype === 'm.text') {
            const sender = evt.sender || 'anonymous';
            const msg: ChatMessage = {
              id: evt.event_id || `matrix-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
              text: evt.content.body || '',
              displayName: sender,
              timestamp: evt.origin_server_ts || Date.now(),
              protocol: 'matrix',
              senderColor: senderColor(sender),
            };
            this.onMessage(msg);
          }
        }
      }
    }
  }

  private async startPollingLoop(): Promise<void> {
    while (!this.isManuallyClosed && !this.pollAbortController?.signal.aborted) {
      try {
        await this.syncOnce(false);
      } catch (err: any) {
        if (this.isManuallyClosed || this.pollAbortController?.signal.aborted || err?.name === 'AbortError') {
          break;
        }
        // Wait before retry on network failure
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
    }
  }
}
